import React, { useState, useEffect } from "react";

const DaisyPetalsGame = () => {
  const [totalPetals, setTotalPetals] = useState(16); // 总花瓣数量
  const [petals, setPetals] = useState(Array(16).fill(true)); // 花瓣状态，true表示存在
  const [currentPlayer, setCurrentPlayer] = useState(1); // 1 or 2
  const [gameMode, setGameMode] = useState("pvp"); // 'pvp', 'ai'
  const [gameStatus, setGameStatus] = useState("playing"); // 'playing', 'finished', 'setup'
  const [showTurnChoice, setShowTurnChoice] = useState(false); // 是否显示先后手选择
  const [editMode, setEditMode] = useState(false); // 编辑模式
  const [winner, setWinner] = useState(null);
  const [selectedPetals, setSelectedPetals] = useState([]); // 选中的花瓣索引
  const [aiMoveMessage, setAiMoveMessage] = useState("");
  const [flashingPetals, setFlashingPetals] = useState([]); // 电脑选择的花瓣闪烁
  const [playerFirst, setPlayerFirst] = useState(true); // 玩家是否先手
  const [errorMessage, setErrorMessage] = useState(""); // 错误提示消息

  // 显示错误提示
  const showError = (message) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(""), 3000); // 3秒后自动消失
  };

  // 初始化花瓣状态 - 所有花瓣都是活跃的，从12点钟位置开始连续排列
  const initializePetals = (total) => {
    return Array(total).fill(true);
  };

  // 进入编辑模式
  const enterEditMode = () => {
    setEditMode(true);
    setShowTurnChoice(false);
  };

  // 退出编辑模式并应用设置
  const exitEditMode = () => {
    setEditMode(false);
    const newPetals = initializePetals(totalPetals);
    setPetals(newPetals);
    resetGameState();
  };

  // 取消编辑模式
  const cancelEditMode = () => {
    setEditMode(false);
    // 恢复到之前的设置
    setTotalPetals(petals.length);
  };

  // 更新总花瓣数量
  const updateTotalPetals = (count) => {
    const newCount = Math.max(3, Math.min(count, 32)); // 限制在3-32之间
    setTotalPetals(newCount);
  };

  // 重置游戏状态（不包括花瓣）
  const resetGameState = () => {
    setCurrentPlayer(1);
    setGameStatus("playing");
    setWinner(null);
    setSelectedPetals([]);
    setAiMoveMessage("");
    setFlashingPetals([]);
  };
  useEffect(() => {
    const remainingPetals = petals.filter((petal) => petal).length;
    if (remainingPetals === 0 && gameStatus === "playing") {
      setGameStatus("finished");
      setWinner(currentPlayer === 1 ? 2 : 1); // 上一个玩家获胜（谁摘掉最后一片）
    }
  }, [petals, currentPlayer, gameStatus]);

  // 检查两个花瓣是否相邻
  const areAdjacent = (index1, index2, currentPetals = petals) => {
    if (!currentPetals[index1] || !currentPetals[index2]) return false;

    const totalPetals = currentPetals.length;
    // 在圆形排列中，相邻意味着索引差1，或者是首尾相邻（0和length-1）
    const diff = Math.abs(index1 - index2);
    return diff === 1 || diff === totalPetals - 1;
  };

  // 找到所有连续的花瓣组
  const findContinuousGroups = (currentPetals = petals) => {
    const groups = [];
    const visited = new Array(currentPetals.length).fill(false);

    for (let i = 0; i < currentPetals.length; i++) {
      if (currentPetals[i] && !visited[i]) {
        const group = [];
        const queue = [i];
        visited[i] = true;

        while (queue.length > 0) {
          const current = queue.shift();
          group.push(current);

          // 检查相邻位置
          const prev =
            (current - 1 + currentPetals.length) % currentPetals.length;
          const next = (current + 1) % currentPetals.length;

          if (currentPetals[prev] && !visited[prev]) {
            visited[prev] = true;
            queue.push(prev);
          }

          if (currentPetals[next] && !visited[next]) {
            visited[next] = true;
            queue.push(next);
          }
        }

        group.sort((a, b) => a - b);
        groups.push(group);
      }
    }

    return groups;
  };

  // 电脑AI移动
  const makeAIMove = () => {
    makeRandomMove();
  };

  // 随机移动
  const makeRandomMove = () => {
    const availableMoves = getAllPossibleMoves();
    if (availableMoves.length === 0) return;

    const randomMove =
      availableMoves[Math.floor(Math.random() * availableMoves.length)];
    makeAIMove_withAnimation(randomMove, "随机选择");
  };

  // 获取所有可能的移动
  const getAllPossibleMoves = (currentPetals = petals) => {
    const moves = [];

    // 单个花瓣移动
    for (let i = 0; i < currentPetals.length; i++) {
      if (currentPetals[i]) {
        moves.push([i]);
      }
    }

    // 相邻两个花瓣移动
    for (let i = 0; i < currentPetals.length; i++) {
      if (currentPetals[i]) {
        const next = (i + 1) % currentPetals.length;
        if (currentPetals[next] && areAdjacent(i, next, currentPetals)) {
          moves.push([i, next]);
        }
      }
    }

    return moves;
  };

  // 带动画的电脑移动
  const makeAIMove_withAnimation = (moveIndexes, strategy) => {
    setFlashingPetals(moveIndexes);
    setAiMoveMessage(`电脑摘掉了${moveIndexes.length}片花瓣 (${strategy})`);

    // 闪烁后移除
    setTimeout(() => {
      makeMove(moveIndexes);
      setFlashingPetals([]);
    }, 1800);
  };

  // 执行移动
  const makeMove = (indexes) => {
    const newPetals = [...petals];
    indexes.forEach((index) => {
      newPetals[index] = false;
    });
    setPetals(newPetals);
    setCurrentPlayer(currentPlayer === 1 ? 2 : 1);
    setSelectedPetals([]);
  };

  // 处理花瓣点击
  const handlePetalClick = (index) => {
    if (gameStatus !== "playing") return;
    if (gameMode === "ai" && isAITurn()) return; // 电脑回合时不允许点击
    if (flashingPetals.length > 0) return; // 电脑移动动画期间不允许点击
    if (!petals[index]) return; // 已被摘掉的花瓣不能点击

    const isSelected = selectedPetals.includes(index);

    if (isSelected) {
      // 取消选择
      setSelectedPetals(selectedPetals.filter((i) => i !== index));
    } else {
      // 添加选择
      if (selectedPetals.length === 0) {
        // 第一个选择
        setSelectedPetals([index]);
      } else if (selectedPetals.length === 1) {
        // 第二个选择，检查是否相邻
        if (areAdjacent(selectedPetals[0], index)) {
          setSelectedPetals([...selectedPetals, index]);
        } else {
          showError('只能选择相邻的花瓣！请重新选择。');
        }
      } else {
        // 已经选择了2个，不能再选
        showError("最多只能选择2片相邻的花瓣！");
      }
    }
  };

  // 判断是否是AI的回合
  const isAITurn = () => {
    if (gameMode !== "ai") return false;
    return (
      (playerFirst && currentPlayer === 2) ||
      (!playerFirst && currentPlayer === 1)
    );
  };

  // 判断是否是玩家的回合
  const isPlayerTurn = () => {
    if (gameMode === "pvp") return true;
    return (
      (playerFirst && currentPlayer === 1) ||
      (!playerFirst && currentPlayer === 2)
    );
  };

  // 确认玩家移动
  const confirmPlayerMove = () => {
    if (selectedPetals.length === 0) {
      showError("请先选择花瓣！");
      return;
    }

    makeMove(selectedPetals);
    setAiMoveMessage(""); // 清除AI移动消息
  };

  // 电脑回合
  useEffect(() => {
    if (gameStatus === "playing" && gameMode === "ai" && isAITurn()) {
      const timer = setTimeout(() => {
        makeAIMove();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [currentPlayer, gameStatus, gameMode, playerFirst]);

  // 重新开始游戏
  const resetGame = () => {
    const newPetals = initializePetals(totalPetals);
    setPetals(newPetals);
    resetGameState();
    // 注意：不重置 showTurnChoice，保持当前的界面状态
  };

  // 选择人机对战模式
  const selectAIMode = () => {
    setShowTurnChoice(true);
  };

  // 开始AI游戏
  const startAIGame = (playerGoesFirst) => {
    setPlayerFirst(playerGoesFirst);
    setGameMode("ai");
    setShowTurnChoice(false);
    resetGame();
  };

  // 开始PVP游戏
  const startPVPGame = () => {
    setGameMode("pvp");
    setPlayerFirst(true); // PVP模式下这个值不重要
    setShowTurnChoice(false);
    resetGame();
  };

  // 返回模式选择
  const backToModeSelection = () => {
    setShowTurnChoice(false);
  };

  // 获取当前玩家显示名称
  const getCurrentPlayerName = () => {
    if (gameMode === "pvp") {
      return `玩家 ${currentPlayer}`;
    } else {
      if (isPlayerTurn()) {
        return "玩家";
      } else {
        return "电脑";
      }
    }
  };

  // 获取获胜者显示名称
  const getWinnerName = () => {
    if (gameMode === "pvp") {
      return `玩家 ${winner}`;
    } else {
      if ((playerFirst && winner === 1) || (!playerFirst && winner === 2)) {
        return "玩家";
      } else {
        return "电脑";
      }
    }
  };

  // 渲染花瓣
  const renderPetal = (index) => {
    const angle = (index * 360) / totalPetals; // 使用totalPetals而不是固定的petals.length
    const radius = 130;
    const x = Math.cos(((angle - 90) * Math.PI) / 180) * radius;
    const y = Math.sin(((angle - 90) * Math.PI) / 180) * radius;

    const isSelected = selectedPetals.includes(index);
    const isFlashing = flashingPetals.includes(index);
    const exists = petals[index];

    // 游戏模式下，只显示存在的花瓣
    if (!exists) return null;

    return (
      <div
        key={index}
        className={`absolute w-8 h-12 cursor-pointer transition-all transform hover:scale-110 ${
          isSelected ? "z-10" : ""
        } ${isFlashing ? "animate-pulse" : ""}`}
        style={{
          left: `calc(50% + ${x}px - 16px)`,
          top: `calc(50% + ${y}px - 45px)`,
          transform: `rotate(${angle}deg)`,
          transformOrigin: "center bottom",
        }}
        onClick={() => handlePetalClick(index)}
      >
        <div
          className={`w-full h-full rounded-full shadow-lg transition-colors ${
            isSelected
              ? "bg-blue-500 ring-2 ring-blue-300"
              : isFlashing
              ? "bg-red-500"
              : "bg-pink-400"
          }`}
          style={{
            background: isSelected
              ? "linear-gradient(to bottom, #3b82f6, #1d4ed8)"
              : isFlashing
              ? "linear-gradient(to bottom, #ef4444, #dc2626)"
              : "linear-gradient(to bottom, #f472b6, #ec4899)",
          }}
        >
          <div
            className={`w-2 h-2 rounded-full mx-auto mt-1 ${
              isSelected
                ? "bg-blue-200"
                : isFlashing
                ? "bg-red-200"
                : "bg-pink-200"
            }`}
          ></div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-gradient-to-b from-green-100 to-green-200 min-h-screen">
      <style jsx>{`
        @keyframes flash {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.3;
          }
        }
      `}</style>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-3xl font-bold text-center mb-6 text-pink-800">
          🌸 Daisy Petals 花瓣游戏
        </h1>

        {/* 游戏模式选择 */}
        {editMode ? (
          // 编辑模式界面
          <div className="mb-6 text-center">
            <div className="flex flex-col items-center gap-6">
              <h2 className="text-2xl font-bold text-purple-700">
                🔧 编辑模式
              </h2>

              <div className="bg-purple-50 rounded-lg p-6 max-w-md">
                {/* 总花瓣数量调整 */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    花瓣数量: {totalPetals}
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => updateTotalPetals(totalPetals - 1)}
                      className="w-8 h-8 bg-red-500 text-white rounded-full hover:bg-red-600 font-bold"
                    >
                      -
                    </button>
                    <input
                      type="range"
                      min="3"
                      max="32"
                      value={totalPetals}
                      onChange={(e) =>
                        updateTotalPetals(parseInt(e.target.value))
                      }
                      className="flex-1"
                    />
                    <button
                      onClick={() => updateTotalPetals(totalPetals + 1)}
                      className="w-8 h-8 bg-green-500 text-white rounded-full hover:bg-green-600 font-bold"
                    >
                      +
                    </button>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">范围: 3-32</div>
                </div>

                {/* 预览信息 */}
                <div className="bg-white rounded p-3 text-sm text-gray-600">
                  <p>
                    <strong>预览:</strong>
                  </p>
                  <p>• 圆圈上将有 {totalPetals} 片花瓣</p>
                  <p>• 所有花瓣相邻排列</p>
                  <p>• 游戏目标：摘掉最后一片花瓣获胜</p>
                </div>
              </div>

              {/* 编辑模式操作按钮 */}
              <div className="flex justify-center gap-4">
                <button
                  onClick={exitEditMode}
                  className="px-6 py-3 bg-purple-500 text-white rounded-lg font-semibold hover:bg-purple-600 transition-colors shadow-lg"
                >
                  ✓ 应用设置
                </button>
                <button
                  onClick={cancelEditMode}
                  className="px-6 py-3 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors"
                >
                  ✗ 取消
                </button>
              </div>
            </div>
          </div>
        ) : !showTurnChoice ? (
          <div className="mb-6 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="flex justify-center gap-4 flex-wrap">
                <button
                  onClick={startPVPGame}
                  className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                    gameMode === "pvp" && !showTurnChoice
                      ? "bg-pink-500 text-white shadow-lg"
                      : "bg-gray-200 hover:bg-gray-300"
                  }`}
                >
                  👥 人与人对战
                </button>
                <button
                  onClick={selectAIMode}
                  className="px-6 py-3 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition-colors shadow-lg"
                >
                  🤖 人机对战
                </button>
                <button
                  onClick={enterEditMode}
                  className="px-6 py-3 bg-purple-500 text-white rounded-lg font-semibold hover:bg-purple-600 transition-colors shadow-lg"
                >
                  🔧 编辑模式
                </button>
              </div>

              {/* 当前模式显示 */}
              {gameMode === "pvp" && !showTurnChoice && (
                <div className="text-sm text-gray-600">
                  🎮 双人对战模式 ({petals.filter((p) => p).length}/
                  {petals.length} 片花瓣)
                </div>
              )}
              {gameMode === "ai" && !showTurnChoice && (
                <div className="text-sm text-gray-600">
                  🤖 人机对战模式 - {playerFirst ? "玩家先手" : "电脑先手"} (
                  {petals.filter((p) => p).length}/{petals.length} 片花瓣)
                </div>
              )}
            </div>
          </div>
        ) : (
          // 先后手选择界面
          <div className="mb-6 text-center">
            <div className="flex flex-col items-center gap-4">
              <h2 className="text-xl font-bold text-gray-700">
                🤖 人机对战 - 选择先后手
              </h2>
              <div className="flex justify-center gap-4">
                <button
                  onClick={() => startAIGame(true)}
                  className="px-6 py-3 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors shadow-lg"
                >
                  ⚡ 玩家先手
                </button>
                <button
                  onClick={() => startAIGame(false)}
                  className="px-6 py-3 bg-purple-500 text-white rounded-lg font-semibold hover:bg-purple-600 transition-colors shadow-lg"
                >
                  🎯 电脑先手
                </button>
              </div>
              <button
                onClick={backToModeSelection}
                className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500 transition-colors text-sm"
              >
                ← 返回模式选择
              </button>
              <div className="text-sm text-gray-600 max-w-md">
                <p>
                  💡 <strong>提示：</strong>
                  先手有一定优势，但需要更谨慎地规划策略
                </p>
                <p>当前设置: {petals.length} 片花瓣</p>
              </div>
            </div>
          </div>
        )}

        {/* 错误提示 */}
        {errorMessage && (
          <div className="fixed top-4 right-4 z-50 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg animate-bounce">
            <div className="flex items-center gap-2">
              <span className="text-xl">⚠️</span>
              <span className="font-medium">{errorMessage}</span>
            </div>
          </div>
        )}

        {/* AI移动消息 */}
        {!editMode && aiMoveMessage && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 font-medium">💻 {aiMoveMessage}</p>
          </div>
        )}

        {/* 游戏状态 */}
        {!editMode && (
          <div className="text-center mb-6">
            {gameStatus === "playing" ? (
              <div>
                <p className="text-xl mb-2">
                  轮到:{" "}
                  <span className="font-bold text-pink-600">
                    {getCurrentPlayerName()}
                  </span>
                </p>
                {gameMode === "ai" && isAITurn() && (
                  <p className="text-gray-600">电脑思考中...</p>
                )}
                {selectedPetals.length > 0 && (
                  <p className="text-sm text-gray-600 mt-1">
                    已选择 {selectedPetals.length} 片花瓣
                  </p>
                )}
              </div>
            ) : (
              <div className="text-2xl font-bold">
                🎉 {getWinnerName()} 获胜！
              </div>
            )}
          </div>
        )}

        {/* 花朵游戏区域 */}
        <div
          className="relative mx-auto mb-6"
          style={{ width: "300px", height: "300px" }}
        >
          {/* 花朵中心 */}
          <div className="absolute top-1/2 left-1/2 w-8 h-8 bg-yellow-400 rounded-full transform -translate-x-1/2 -translate-y-1/2 shadow-lg border-2 border-yellow-500">
            <div className="w-4 h-4 bg-yellow-200 rounded-full mx-auto mt-1"></div>
          </div>

          {/* 花瓣 */}
          {Array.from({ length: totalPetals }, (_, index) =>
            renderPetal(index)
          )}

          {/* 剩余花瓣数量显示 */}
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 bg-white rounded-full px-3 py-1 shadow text-sm">
            剩余: {petals.filter((p) => p).length} 片
          </div>
        </div>

        {/* 操作按钮 */}
        {!editMode &&
          gameStatus === "playing" &&
          selectedPetals.length > 0 &&
          (gameMode === "pvp" || isPlayerTurn()) && (
            <div className="flex justify-center gap-4 mb-4">
              <button
                onClick={confirmPlayerMove}
                className="px-6 py-3 bg-pink-500 text-white rounded-lg hover:bg-pink-600 font-bold"
              >
                确认摘取 ({selectedPetals.length} 片)
              </button>
              <button
                onClick={() => setSelectedPetals([])}
                className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                取消选择
              </button>
            </div>
          )}

        {/* 重新开始 */}
        {!editMode && (
          <div className="text-center mb-6 mt-8">
            <button
              onClick={resetGame}
              className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 font-bold"
            >
              重新开始
            </button>
          </div>
        )}

        {/* 游戏规则 */}
        <div className="p-4 bg-pink-50 rounded-lg">
          <h3 className="font-bold mb-2 text-pink-800">游戏规则:</h3>
          <ul className="text-sm space-y-1">
            <li>• 点击花瓣来选择，选中的花瓣会变蓝色</li>
            <li>• 每次可以摘1片花瓣，或者相邻的2片花瓣</li>
            <li>
              • 注意：只有开始时相邻的花瓣才算相邻，中间被摘掉后就不相邻了
            </li>
            <li>• 摘掉最后一片花瓣的玩家获胜</li>
          </ul>
        </div>
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default DaisyPetalsGame;
